import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAssetFavorite } from '@/hooks/useAssetFavorite';

interface FavoriteButtonProps {
  assetId: string;
  isFavorited: boolean;
  variant?: 'icon' | 'button';
  className?: string;
}

export function FavoriteButton({
  assetId,
  isFavorited,
  variant = 'icon',
  className
}: FavoriteButtonProps) {
  // Local state for optimistic updates
  const [localFavorited, setLocalFavorited] = useState(isFavorited);
  const { toggleFavorite, isToggling } = useAssetFavorite(assetId, isFavorited);

  // Sync local state with prop changes (from backend updates)
  useEffect(() => {
    setLocalFavorited(isFavorited);
  }, [isFavorited]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Optimistically update local state immediately
    setLocalFavorited(!localFavorited);
    toggleFavorite();
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        disabled={isToggling}
        className={cn(
          "p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50",
          localFavorited && "text-yellow-500",
          !localFavorited && "text-gray-400",
          className
        )}
        title={localFavorited ? "Remove from favorites" : "Add to favorites"}
      >
        <Star
          className={cn(
            "w-5 h-5 transition-all",
            localFavorited && "fill-current"
          )}
        />
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={isToggling}
      className={cn(
        "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
        localFavorited
          ? "bg-yellow-50 text-yellow-700 hover:bg-yellow-100"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200",
        "disabled:opacity-50",
        className
      )}
    >
      <Star
        className={cn(
          "w-4 h-4",
          localFavorited && "fill-current"
        )}
      />
      {localFavorited ? 'Favorited' : 'Favorite'}
    </button>
  );
}
